import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { usePersistedOpen } from '@/hooks/use-persisted-open';
import { cn } from '@/utils/cn';

/**
 * A page-level metrics block that thins out when collapsed — never disappears.
 *
 * Collapsed: a bordered strip with the title and a one-line summary of the key
 * figures, so people still see that an overview is there. Expanded: the full
 * tiles and charts.
 *
 * List pages share one open/closed preference. The dashboard must pass its own
 * `storageKey` — collapsing Overview on Giving/People must not empty home.
 */

/** Shared preference for list-page Overview panels. */
const INSIGHTS_OPEN_STORAGE_KEY = 'trinity.insights.open';

/** Dashboard home — independent of the list-page preference. */
export const DASHBOARD_INSIGHTS_OPEN_STORAGE_KEY = 'trinity.insights.dashboard.open';

export interface InsightsSummaryItem {
  label: string;
  value: ReactNode;
}

export interface CollapsibleInsightsProps {
  /** Header label. Defaults to the shared "Overview" string. */
  title?: string;
  /**
   * Compact figures shown in the thin strip when collapsed.
   * Pass the same headline numbers the expanded tiles lead with.
   */
  summary?: InsightsSummaryItem[];
  /** Whether the section starts open when no preference is stored. */
  defaultOpen?: boolean;
  /**
   * localStorage key for open state. Defaults to the shared list-page key.
   * Pass a distinct key when Overview is the page’s main content (dashboard).
   */
  storageKey?: string;
  children: ReactNode;
  className?: string;
}

export const InsightsSummary = ({ items }: { items: InsightsSummaryItem[] }) => {
  if (items.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
      {items.map((item) => (
        <div
          key={item.label}
          className="flex min-w-0 max-w-full items-baseline gap-1.5 text-caption"
        >
          <span className="shrink-0 text-fg-secondary">{item.label}</span>
          <span className="min-w-0 truncate font-medium tabular-nums text-fg">{item.value}</span>
        </div>
      ))}
    </div>
  );
};

export const CollapsibleInsights = ({
  title,
  summary,
  defaultOpen = true,
  storageKey = INSIGHTS_OPEN_STORAGE_KEY,
  children,
  className,
}: CollapsibleInsightsProps) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = usePersistedOpen(storageKey, defaultOpen);
  const label = title ?? t('insights.title');
  const summaryItems = summary?.filter((item) => item.value !== undefined && item.value !== null) ?? [];

  return (
    <section
      className={cn(
        'mb-6 overflow-hidden rounded-lg border border-border bg-surface',
        className,
      )}
    >
      <button
        type="button"
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-surface-sunken/60"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="shrink-0 text-body font-medium text-fg">{label}</span>

        {!open && summaryItems.length > 0 ? (
          <div className="min-w-0 flex-1">
            <InsightsSummary items={summaryItems} />
          </div>
        ) : (
          <span className="min-w-0 flex-1" />
        )}

        <span className="inline-flex shrink-0 items-center gap-1 text-caption text-fg-secondary">
          {open ? t('insights.hide') : t('insights.show')}
          <ChevronDown
            aria-hidden
            className={cn('size-4 transition-transform', open ? 'rotate-180' : 'rotate-0')}
          />
        </span>
      </button>

      {open ? (
        <div className="space-y-4 border-t border-border px-4 py-4">{children}</div>
      ) : null}
    </section>
  );
};
